import { useEffect, useMemo, useState } from 'react'
import {
  BatchImport,
  ClientOrganization,
  callSearchBatchImport,
  callSearchClientOrganizations,
  findAutomationGroup,
  searchFlux,
  type Group,
} from '@/api'
import { paths } from '@/paths'
import { mapAutomationGroupToReferential } from '@/sections/referentials/utils/mapAutomationGroupToReferential'
import { filterReferentials } from '@/sections/referentials/utils/filterReferentials'
import { FeatureNames } from '@/lib/featureFlags'
import { useAuth } from '@/hooks/use-auth'
import { useFetchCurrentUserGroups } from '@/hooks/use-fetch-current-user-groups'
import type { CommandBarItem } from '../types'
import { useFeatureFlags } from '@/hooks/use-feature-flags'

const SEARCH_SIZE = 5
const SEARCH_DEBOUNCE_MS = 300
const EMPTY_GROUPS: Group[] = []
const EMPTY_ENTITY_RESULTS = {
  documents: [] as CommandBarItem[],
  clients: [] as CommandBarItem[],
  referentials: [] as CommandBarItem[],
  flux: [] as CommandBarItem[],
}

function buildDocumentSubtitle(document: BatchImport): string | undefined {
  const parts = [
    document.configuration?.name,
    document.clientOrganization?.name,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}

function buildClientSubtitle(
  client: ClientOrganization,
  groupsById: Record<string, Group>
): string | undefined {
  const groupId = client.group?.[0]
  if (!groupId) return undefined
  return groupsById[groupId]?.name
}

async function searchDocuments(query: string): Promise<CommandBarItem[]> {
  try {
    const response = await callSearchBatchImport({
      name: query,
      size: SEARCH_SIZE,
    })
    const documents = response?.data ?? []
    return documents
      .filter((doc) => doc.fbid)
      .map((doc) => ({
        id: `document-${doc.fbid}`,
        type: 'document' as const,
        label: doc.metadata?.dataSetName ?? doc.fbid ?? '',
        subtitle: buildDocumentSubtitle(doc),
        entityId: doc.fbid,
        path: paths.dashboard.documents.batchImport(doc.fbid!),
      }))
  } catch {
    return []
  }
}

async function searchClients(
  query: string,
  groupsById: Record<string, Group>
): Promise<CommandBarItem[]> {
  try {
    const response = await callSearchClientOrganizations({
      name: query,
      size: SEARCH_SIZE,
    })
    const clients = response?.data ?? []
    return clients
      .filter((client) => client.coid)
      .map((client) => ({
        id: `client-${client.coid}`,
        type: 'client' as const,
        label: client.name ?? client.coid ?? '',
        subtitle: buildClientSubtitle(client, groupsById),
        entityId: client.coid,
        path: `${paths.dashboard.customerFlow.customer}/${client.coid}`,
      }))
  } catch {
    return []
  }
}

async function searchFluxItems(
  query: string,
  groupsById: Record<string, Group>
): Promise<CommandBarItem[]> {
  try {
    const response = await searchFlux({
      name: query,
      size: SEARCH_SIZE,
    })
    const fluxList = response?.data ?? []
    return fluxList
      .filter((flux) => flux.fid)
      .map((flux) => ({
        id: `flux-${flux.fid}`,
        type: 'flux' as const,
        label: flux.name ?? flux.fid ?? '',
        subtitle: flux.groupId ? groupsById[flux.groupId]?.name : undefined,
        entityId: flux.fid,
        path: `${paths.dashboard.customerFlow.flow}/${flux.fid}`,
      }))
  } catch {
    return []
  }
}

async function searchReferentials(
  query: string,
  groups: Group[]
): Promise<CommandBarItem[]> {
  try {
    const responses = await Promise.all(
      groups
        .filter((group) => group.gid)
        .map(async (group) => {
          const response = await findAutomationGroup({ groupId: group.gid })
          const items = response?.data ?? []
          return items.map((item) =>
            mapAutomationGroupToReferential(item, group.gid!)
          )
        })
    )

    const referentials = responses.flat()
    const filtered = filterReferentials(referentials, {
      search: query,
      clientFilter: 'all',
      statusFilter: 'all',
    })

    return filtered.slice(0, SEARCH_SIZE).map((referential) => ({
      id: `referential-${referential.id}`,
      type: 'referential' as const,
      label: referential.name,
      subtitle: referential.clientOrganizationId
        ? undefined
        : undefined,
      entityId: referential.id,
      path: paths.dashboard.customerFlow.referentialDetail(referential.id),
    }))
  } catch {
    return []
  }
}

export function useCommandBarSearch(query: string) {
  const { user } = useAuth()
  const { isFeatureEnabled } = useFeatureFlags()
  const { data: groupsData } = useFetchCurrentUserGroups()
  const groups = groupsData ?? EMPTY_GROUPS
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  const [isSearching, setIsSearching] = useState(false)
  const [entityResults, setEntityResults] = useState(EMPTY_ENTITY_RESULTS)

  const showReferentials = isFeatureEnabled(FeatureNames.referentials)

  const groupsById = useMemo(
    () =>
      groups.reduce<Record<string, Group>>((acc, group) => {
        if (group.gid) acc[group.gid] = group
        return acc
      }, {}),
    [groups]
  )

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!debouncedQuery) {
      setEntityResults((prev) =>
        prev.documents.length === 0 &&
        prev.clients.length === 0 &&
        prev.referentials.length === 0 &&
        prev.flux.length === 0
          ? prev
          : EMPTY_ENTITY_RESULTS
      )
      setIsSearching((prev) => (prev ? false : prev))
      return
    }

    let cancelled = false
    setIsSearching(true)

    void (async () => {
      const [documents, clients, fluxItems, referentials] = await Promise.all([
        searchDocuments(debouncedQuery),
        searchClients(debouncedQuery, groupsById),
        searchFluxItems(debouncedQuery, groupsById),
        showReferentials
          ? searchReferentials(debouncedQuery, groups)
          : Promise.resolve([]),
      ])

      if (!cancelled) {
        setEntityResults({ documents, clients, referentials, flux: fluxItems })
        setIsSearching(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, groups, groupsById, showReferentials])

  return {
    isSearching,
    hasQuery: debouncedQuery.length > 0,
    entityResults,
    showReferentials,
  }
}
